import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import sr from '@utils/sr';
import { srConfig } from '@config';

const StyledCertificatesSection = styled.section`
  max-width: 1000px;
  margin: 0 auto 100px;
  text-align: center;

  h2 {
    font-size: clamp(24px, 5vw, var(--fz-heading));
  }

  .certificates-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 30px;
    margin-top: 50px;

    @media (max-width: 768px) {
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 20px;
    }
  }

  .certificate-card {
    background: var(--light-navy);
    border-radius: 8px;
    padding: 30px 20px;
    transition: var(--transition);
    cursor: pointer;
    border: 2px solid transparent;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    min-height: 280px;

    &:hover {
      transform: translateY(-5px);
      border-color: var(--green);
      background: var(--lightest-navy);
    }

    .certificate-icon {
      width: 100px;
      height: 100px;
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: var(--navy);
      transition: var(--transition);
      padding: 20px;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    &:hover .certificate-icon {
      background: var(--green-tint);
      transform: scale(1.1);
    }

    .certificate-title {
      font-size: var(--fz-lg);
      font-weight: 600;
      margin-bottom: 10px;
      color: var(--lightest-slate);
    }

    .certificate-issuer {
      font-size: var(--fz-sm);
      color: var(--slate);
      margin-bottom: 15px;
    }

    .certificate-date {
      font-size: var(--fz-xs);
      color: var(--green);
      font-family: var(--font-mono);
    }

    .view-certificate {
      margin-top: 15px;
      font-size: var(--fz-xs);
      color: var(--green);
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 5px;

      svg {
        width: 12px;
        height: 12px;
        fill: currentColor;
      }
    }
  }
`;

const Certificates = () => {
  const revealTitle = useRef(null);
  const revealCertificates = useRef([]);

  useEffect(() => {
    sr.reveal(revealTitle.current, srConfig());
    revealCertificates.current.forEach((el, i) => sr.reveal(el, srConfig(i * 100)));
  }, []);

  const certificates = [
    {
      title: 'Terraform Associate',
      issuer: 'HashiCorp',
      date: '2025',
      pdf: '/TerraformAssociate00320251115-30-9t2s57.pdf',
      icon: (
        <img
          src="/icons/hashicorp-terraform.png"
          alt="Terraform"
          style={{ width: '50px', height: '50px' }}
        />
      ),
    },
    {
      title: 'AWS Certified Cloud Practitioner',
      issuer: 'Amazon Web Services',
      date: '2026',
      pdf: '/AWS Certified Cloud Practitioner certificate.pdf',
      icon: <img src="/icons/1583149.webp" alt="AWS" style={{ width: '50px', height: '50px' }} />,
    },
    {
      title: 'LPIC-1',
      issuer: 'Linux Professional Institute',
      date: '2024',
      pdf: '/LPIC-1.pdf',
      icon: (
        <img src="/icons/linux-logo.jpg" alt="Linux" style={{ width: '50px', height: '50px' }} />
      ),
    },
    {
      title: 'EF SET Certificate',
      issuer: 'EF Education First',
      date: '2024',
      pdf: '/EF SET Certificate.pdf',
      icon: (
        <img src="/icons/ef-set-logo.png" alt="EF SET" style={{ width: '50px', height: '50px' }} />
      ),
    },
    {
      title: 'Java Certificate',
      issuer: 'Oracle',
      date: '2025',
      pdf: '/javaeCertificate.pdf',
      icon: <img src="/icons/1174953.webp" alt="Java" style={{ width: '50px', height: '50px' }} />,
    },
    {
      title: 'Kubernetes Basics',
      issuer: 'SUSE',
      date: '2025',
      pdf: '/Ade Ndoh Micheal - 2025-12-18.pdf',
      icon: (
        <img src="/icons/suse-linux-.jpg" alt="SUSE" style={{ width: '50px', height: '50px' }} />
      ),
    },
  ];

  return (
    <StyledCertificatesSection id="certificates">
      <h2 className="numbered-heading" ref={revealTitle}>
        Certifications
      </h2>

      <div className="certificates-grid">
        {certificates.map((cert, i) => (
          <div
            key={i}
            className="certificate-card"
            ref={el => (revealCertificates.current[i] = el)}
            onClick={() => window.open(cert.pdf, '_blank')}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                window.open(cert.pdf, '_blank');
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`View ${cert.title} certificate`}>
            <div className="certificate-icon">{cert.icon}</div>
            <h3 className="certificate-title">{cert.title}</h3>
            <p className="certificate-issuer">{cert.issuer}</p>
            <p className="certificate-date">{cert.date}</p>
            <div className="view-certificate">
              <svg viewBox="0 0 24 24">
                <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zM16 19H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
              </svg>
              View Certificate
            </div>
          </div>
        ))}
      </div>
    </StyledCertificatesSection>
  );
};

export default Certificates;
